export default defineNuxtPlugin(async (nuxtApp) => {
  const rootStore = useRootStore();
  const { EcomService } = await import('~/services/ecom.service');

  // Local state for refresh control
  let isRefreshInProgress = false;

  // Utility: get token data from cookie
  function getAccessTokenData() {
    const cookie = useCookie<{
      accessToken: string;
      expiresTime: number;
    }>('ecomApiAccessToken');
    return cookie.value;
  }

  // Utility: set token data cookie and update store + injection
  function updateAccessToken(tokenData: { accessToken: string; expiresTime: number }) {
    const cookie = useCookie('ecomApiAccessToken', {
      expires: new Date(tokenData.expiresTime * 1000),
      secure: true,
      sameSite: 'lax', // Adjust as needed: 'strict' or 'none' if cross-site
      // httpOnly: true, // Can't use if cookie accessed client-side in JS, so omit
    });
    cookie.value = tokenData;

    // Update rootStore and inject for reactivity/usage
    rootStore.access_token = tokenData.accessToken;
  }

  // Token validity check convenience
  function isValidToken(tokenData?: { accessToken: string; expiresTime: number }) {
    if (!tokenData) return false;
    return EcomService.isTokenValid(
        tokenData.accessToken,
        tokenData.expiresTime,
        EcomService.accessTokenExpiryBuffer
    );
  }

  // Calculate refresh interval in milliseconds
  function calculateRefreshInterval(): number {
    const tokenData = getAccessTokenData();
    if (!tokenData?.expiresTime) {
      return EcomService.accessTokenPollInterval * 1000;
    }

    const nowSec = Math.floor(Date.now() / 1000);
    const timeLeft = tokenData.expiresTime - nowSec;
    if (timeLeft <= 0) {
      // Token expired, refresh immediately (short delay)
      return 5000; // 5 seconds to retry quickly
    }

    // Ideally refresh at 75% of remaining time or configured buffer, whichever is smaller
    // But never refresh more frequently than every 1 minute
    const candidate = Math.min(timeLeft * 0.75, EcomService.accessTokenExpiryBuffer);
    const minIntervalSec = 60; // 1 minute minimal wait
    const intervalSec = Math.max(candidate, minIntervalSec);

    return intervalSec * 1000; // ms
  }

  // Schedule refresh token with adaptive timing
  function scheduleNextRefresh(delayMs: number) {
    if (rootStore.accessTokenTimeoutId) {
      clearTimeout(rootStore.accessTokenTimeoutId);
    }

    rootStore.accessTokenTimeoutId = setTimeout(async () => {
      if (isRefreshInProgress) {
        // Another refresh happening; reschedule shortly
        scheduleNextRefresh(30000); // 30 seconds
        return;
      }

      try {
        const tokenData = getAccessTokenData();
        if (!isValidToken(tokenData)) {
          isRefreshInProgress = true;
          const newTokenData = await EcomService.fetchToken();

          if (newTokenData?.accessToken && newTokenData.expiresTime) {
            updateAccessToken(newTokenData);
            console.info('[EcomService] Access token refreshed');
          } else {
            console.warn('[EcomService] Fetched token data is incomplete:', newTokenData);
          }

          isRefreshInProgress = false;
        } else {
          // Token is still valid, update rootStore & injection just in case
          if (tokenData?.accessToken) {
            rootStore.access_token = tokenData.accessToken;
          }
        }

        // Schedule next refresh based on new timing
        scheduleNextRefresh(calculateRefreshInterval());
      } catch (error) {
        console.error('[EcomService] Failed to refresh access token:', error);
        isRefreshInProgress = false;

        // Retry in 5 minutes on failure
        scheduleNextRefresh(300000);
      }
    }, delayMs);
  }

  // Sync reactive store and injection when cookie changes client-side
  if (import.meta.client) {
    // Watch the access token cookie to keep rootStore and inject in sync
    const tokenCookie = useCookie('ecomApiAccessToken');
    watch(
        () => tokenCookie.value,
        (newVal) => {
          if (newVal?.accessToken) {
            rootStore.access_token = newVal.accessToken;
            // Optional: console.log('Access token updated from cookie change');
          }
        },
        { immediate: true }
    );
  }

  // Server-side initial token check & fetch if needed
  if (import.meta.server) {
    const tokenData = getAccessTokenData();
    if (!isValidToken(tokenData)) {
      try {
        const fetched = await EcomService.fetchToken();
        if (fetched?.accessToken && fetched.expiresTime) {
          updateAccessToken(fetched);
          nuxtApp.provide('ecomAccessToken', fetched.accessToken);
        } else {
          console.warn('[EcomService] Initial fetched token incomplete:', fetched);
        }
      } catch (error) {
        console.error('[EcomService] Initial token fetch failed on server:', error);
      }
    } else {
      // Valid token found, sync states
      if (tokenData?.accessToken) {
        rootStore.access_token = tokenData.accessToken;
        nuxtApp.provide('ecomAccessToken', tokenData.accessToken);
      }
    }
  }

  // Client-side: start refresh cycle
  if (import.meta.client) {
    const initialDelay = calculateRefreshInterval();
    scheduleNextRefresh(initialDelay);
  }
});